import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isEmpty } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IBackendCartEntries, ICartEntry, ISanityVendorConfigs } from '@rbi-ctg/menu';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import ModalOrderUnavailable from 'components/modal-order-unavailable';
import { RecentOrderCard } from 'components/recent-order-card';
import { RecentOrderCardCarousel } from 'components/recent-order-card-carousel';
import { RecentOrderCardWithModifiers } from 'components/recent-order-card-with-modifiers';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import useEffectOnce from 'hooks/use-effect-once';
import { useGetSanityImagesForCartEntries } from 'hooks/use-get-sanity-images-for-cart-entries';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import {
  ICartEntriesMenuObjectEntry,
  useCartEntriesMenuObjects,
} from 'state/order/hooks/use-cart-entries-menu-objects';
import { useGetMenuItemVendorConfigForCartEntries } from 'state/order/hooks/use-get-menu-item-vendor-config-for-cart-entries';
import { useGetUnavailableItemsFromMenu } from 'state/order/hooks/use-unavailable-cart-entries';
import { isOfferType } from 'state/order/utils';
import { useStoreContext } from 'state/store';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { maybeGetPickerIdFromCartEntry } from 'utils/menu/maybe-get-picker-id-from-cart-entry';
import { priceForItemOptionModifierPlu } from 'utils/menu/price';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { Container } from './styled';
import { IBackendCartEntriesWithAvailability, IRecentItemsProps } from './types';

type GetCartEntriesMenuObjects = (
  cartEntries: ICartEntry[]
) => Promise<ICartEntriesMenuObjectEntry[]>;

const RecentItems: FC<IRecentItemsProps> = ({ locationName, containerHasTopPadding = true }) => {
  const [selectionUnavailable, setSelectionUnavailable] = useState<ICartEntry | null>(null);
  const getUnavailableItemsFromMenu = useGetUnavailableItemsFromMenu();
  const { getCartEntriesMenuObjects } = useCartEntriesMenuObjects();
  const getCartEntriesMenuObjectsRef = useRef<GetCartEntriesMenuObjects>(getCartEntriesMenuObjects);
  getCartEntriesMenuObjectsRef.current = getCartEntriesMenuObjects;
  const getMenuItemVendorConfigForCartEntries = useGetMenuItemVendorConfigForCartEntries();
  const [isLoading, setIsLoading] = useState(true);
  const [recentItemIndex, setRecentItemIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<IBackendCartEntriesWithAvailability[]>([]);
  const { getMainMenu, pricingFunction } = useMenuContext();
  const { user, refreshCurrentUser } = useAuthContext();
  const mParticle = useMParticleContext();
  const { location, setStoreLocatorCallbackUrl } = useLocationContext();
  const enableEditRecentItem = useFlag(LaunchDarklyFlag.ENABLE_EDIT_RECENT_ITEM);
  const enableRecentItemWithModifiers = useFlag(
    LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS
  );
  const sanityImageMap = useGetSanityImagesForCartEntries(user?.uniquePurchasedItems);
  const {
    recent: { pendingRecentItem, setPendingRecentItem },
    addToCart,
  } = useOrderContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  useEffectOnce(() => {
    refreshCurrentUser();
  });

  const mainMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const addItemToCart = useCallback(
    (item: IBackendCartEntries) => {
      const curriedPriceItemOptionModifier = (currentItem: IBackendCartEntries) => (
        modifier: IBackendCartEntries
      ) =>
        priceForItemOptionModifierPlu({
          item: currentItem,
          modifier,
          prices,
          vendor,
        });

      const itemMapped = mapBackendToFrontend(
        pricingFunction,
        curriedPriceItemOptionModifier
      )(item);

      if (itemMapped !== null) {
        addToCart(itemMapped);
      } else {
        toast.error(formatMessage({ id: 'addToCartError' }, { itemName: item.name }));
      }
    },
    [addToCart, formatMessage, prices, pricingFunction, vendor]
  );

  const routeStoreToInitialLocation = useCallback(() => {
    setStoreLocatorCallbackUrl(location.pathname);
    navigate(routes.storeLocator);
  }, [location.pathname, navigate, setStoreLocatorCallbackUrl]);

  const logAddRecentItemToCart = useCallback(
    (name: string, productPosition: number, numItems: number) => {
      mParticle.logEvent(CustomEventNames.ADD_RECENT_ITEM_TO_CART, EventTypes.UserContent, {
        'Product Name': name,
        'Product Position': productPosition,
        'Number of recent items': numItems,
        'User Location': locationName,
      });
    },
    [locationName, mParticle]
  );

  const addAndLogItemToCart = useCallback(
    (item: IBackendCartEntries, index: number) => {
      const { name } = item;
      addItemToCart(item);
      if (name) {
        logAddRecentItemToCart(name, index + 1, recentItems.length);
        setRecentItemIndex(index + 1);
      }

      hapticImpact({ style: HapticsImpactStyle.Heavy });
    },
    [addItemToCart, logAddRecentItemToCart, recentItems.length]
  );

  const getProductImage = useCallback(
    (item: IBackendCartEntriesWithAvailability) => {
      const image = sanityImageMap[item.sanityId];
      if (image?.asset) {
        return image;
      }
      // If item doesn't have image, check if a picker image exists
      const pickerId = maybeGetPickerIdFromCartEntry(item);
      return sanityImageMap[pickerId];
    },
    [sanityImageMap]
  );

  const addToCartIfEntryAvailable = useCallback(
    async (item: IBackendCartEntriesWithAvailability, index: number) => {
      // Clear out pending item
      setPendingRecentItem(null);
      const cartEntryFromBECartEntry = {
        ...item,
        _id: `${item.type}-${item.sanityId}`,
        cartId: '',
        children: [],
        name: item.name ?? '',
        pickerSelections: {},
      } as ICartEntry;
      // Check if item is available for the selected store
      const res = await getCartEntriesMenuObjectsRef.current([cartEntryFromBECartEntry]);
      // If there is no data, then the item is not available
      if (res[0]?.data) {
        addAndLogItemToCart(item, index);
      } else {
        setSelectionUnavailable(cartEntryFromBECartEntry);
      }
    },
    [setPendingRecentItem, addAndLogItemToCart]
  );

  useEffect(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem && prices) {
      addToCartIfEntryAvailable(pendingRecentItem, recentItemIndex);
    }
  }, [
    isStoreOpenAndAvailable,
    pendingRecentItem,
    addToCartIfEntryAvailable,
    recentItemIndex,
    prices,
  ]);

  const handleAddToCart = (item: IBackendCartEntriesWithAvailability, index: number) => {
    if (isStoreOpenAndAvailable) {
      setRecentItemIndex(index);
      addToCartIfEntryAvailable(item, index);
    } else {
      setPendingRecentItem({ ...item, image: getProductImage(item) });
      routeStoreToInitialLocation();
    }
  };
  const uniqueItems = user?.uniquePurchasedItems || [];

  useEffect(() => {
    if (uniqueItems.length) {
      const curriedPriceItemOptionModifier = (item: IBackendCartEntries) => (
        modifier: IBackendCartEntries
      ) =>
        priceForItemOptionModifierPlu({
          item,
          modifier,
          prices,
          vendor,
        });

      const cartEntriesMappedToFrontend = uniqueItems.map(
        mapBackendToFrontend(pricingFunction, curriedPriceItemOptionModifier)
      );

      // If prices aren't available, all items will be identified as unavailable
      // instead mark them as available to make sure we never fire the error modal
      if (isEmpty(prices)) {
        const mappedItems = uniqueItems.map(currentItem => ({
          ...currentItem,
          isAvailable: true,
          isDisabled: isOfferType(currentItem.type),
          lineId: '',
        }));
        setRecentItems(mappedItems);
        setIsLoading(false);
        return;
      }
      let mappedVendorConfigs = {} as Record<string, ISanityVendorConfigs>;
      getMenuItemVendorConfigForCartEntries(cartEntriesMappedToFrontend)
        .then(({ items }) => {
          mappedVendorConfigs = items;
          return getUnavailableItemsFromMenu(cartEntriesMappedToFrontend);
        })
        .then(({ items }) => {
          const itemIdsSet = new Set(items.map(item => item._id));
          const recentItemsWithAvail = uniqueItems.reduce(
            (acc: Array<IBackendCartEntriesWithAvailability>, currentItem: IBackendCartEntries) => {
              const isAvailable = !itemIdsSet.has(currentItem.sanityId);
              acc.push({
                ...currentItem,
                vendorConfigs: mappedVendorConfigs[currentItem.sanityId],
                isAvailable,
                isDisabled: isOfferType(currentItem.type),
                lineId: '',
              });
              return acc;
            },
            []
          );
          setRecentItems(recentItemsWithAvail);
        })
        .finally(() => setIsLoading(false));
    }
  }, [
    getUnavailableItemsFromMenu,
    getMenuItemVendorConfigForCartEntries,
    prices,
    pricingFunction,
    uniqueItems,
    vendor,
  ]);
  const handleAddClick = (item: IBackendCartEntriesWithAvailability, index: number) => () =>
    handleAddToCart(item, index);
  const createAriaLabel = useCreateOrderAriaLabel();
  const backgroundImage = mainMenu.data?.pickerBackgroundImage;
  const getIsAvailable = ({ isAvailable }: { isAvailable: boolean }) =>
    isStoreOpenAndAvailable ? isAvailable : true;
  return (
    <>
      <Container $containerHasTopPadding={containerHasTopPadding}>
        {isLoading ? (
          <LoadingContainer>
            <LoadingAnimation fillColor={Styles.color.primary} />
          </LoadingContainer>
        ) : (
          <RecentOrderCardCarousel bgColor="transparent">
            {recentItems.map((item: IBackendCartEntriesWithAvailability, index: number) =>
              enableRecentItemWithModifiers ? (
                <RecentOrderCardWithModifiers
                  key={item.sanityId}
                  handleAddClick={handleAddClick(item, index)}
                  item={item}
                  productImage={getProductImage(item)}
                  backgroundImage={backgroundImage}
                  isAvailable={getIsAvailable(item)}
                />
              ) : (
                <RecentOrderCard
                  key={item.sanityId}
                  isAvailable={getIsAvailable(item) && !item.isDisabled}
                  name={item.name || ''}
                  productImage={getProductImage(item)}
                  backgroundImage={backgroundImage}
                  addToCartOnClick={handleAddClick(item, index)}
                  showEdit={enableEditRecentItem}
                  ariaLabel={createAriaLabel({
                    buttonType: 'add',
                    name: item.name,
                  })}
                />
              )
            )}
          </RecentOrderCardCarousel>
        )}
      </Container>
      {selectionUnavailable && (
        <ModalOrderUnavailable
          items={[selectionUnavailable]}
          onDismiss={() => setSelectionUnavailable(null)}
        />
      )}
    </>
  );
};

export default RecentItems;
